(ns practitest.frontend.components.datepicker2
  "Date pickers for the new UI.
  A text input shows dd-MMM-yyyy format, backed by a visually hidden native input
  that only hosts the browser's own calendar popup.
  Native date inputs don't support custom display format.
  `datepicker/instant-picker` is built on `native-picker-input` from here."
  (:require
    [clojure.string :as str]
    [practitest.frontend.common.dom :as dom]
    [practitest.frontend.common.format-utils :as fu]
    [practitest.frontend.components.icons :as icons]
    [reagent.core :as reagent]
    [tick.core :as tick]))

;; yyyy-MM-dd is the format <input type="date"> requires for its value attribute
(def ^:private native-date-formatter (tick/formatter "yyyy-MM-dd"))
;; <input type="datetime-local"> value format; does not support seconds
(def native-date-time-formatter (tick/formatter "yyyy-MM-dd'T'HH:mm"))

;; placeholders, similar to how it looks in native inputs
(def date-placeholder "dd-mmm-yyyy")
(def date-time-placeholder (str date-placeholder " --:--"))

(defn- as-tick-date
  [value]
  (when value
    (cond
      (tick/date? value) value

      (string? value)
      (let [trimmed (str/trim value)]
        (when-not (str/blank? trimmed)
          (try
            ;; Handles both plain "2022-01-02" and instant-like
            ;; "2024-03-01T04:00:00+02:00" strings by taking the date portion
            (tick/date (subs trimmed 0 (min 10 (count trimmed))))
            (catch js/Error _ nil))))

      :else
      (try (tick/date value) (catch js/Error _ nil)))))

(defn- format-date-for-native-input
  [value]
  (when-let [date (as-tick-date value)]
    (tick/format native-date-formatter date)))

(defn- parse-native-input-value
  [datestr]
  (when-not (str/blank? datestr)
    (try
      (tick/date datestr)
      (catch js/Error _ nil))))

(defn- normalize-typed-date [s]
  (str/replace s #"^(\d{1,2})-([A-Za-z]{3})-"
               (fn [[_ day month]]
                 (str (fu/format-integer (js/parseInt day 10) 2) "-" (str/capitalize month) "-"))))

(defn- try-parse-typed
  "nil instead of throwing; the text input parses on every keystroke."
  [parse-fn s]
  (when-not (str/blank? s)
    (try
      (parse-fn (normalize-typed-date (str/trim s)))
      (catch js/Error _ nil))))

(defn parse-typed-date
  "\"02-Sep-2026\" or \"2026-09-02\" -> tick date; nil while still typing or on garbage."
  [s]
  (or (try-parse-typed #(tick/parse-date % fu/local-date-formatter) s)
      (parse-native-input-value (str/trim (str s)))))

(defn parse-typed-date-time
  "\"02-Sep-2026 14:30\" -> tick date-time; date without time means midnight.
  nil while still typing or on garbage."
  [s]
  (or (try-parse-typed #(tick/parse-date-time % fu/date-time-formatter) s)
      (some-> (parse-typed-date s) (tick/at "00:00"))))

(defn parse-native-date-time-value
  "\"2026-09-02T14:30\" from <input type=\"datetime-local\"> -> tick date-time; nil on blank."
  [s]
  (when-not (str/blank? s)
    (try
      (tick/date-time s)
      (catch js/Error _ nil))))

(defn- open-native-picker!
  "showPicker() needs a user gesture and throws in cross-origin iframes and on old browsers.
  nothing useful to do here, so swallow the error."
  [input]
  (when input
    (try
      ;; js-invoke (not .showPicker): the CLJS compiler can't infer input's type
      ;; well enough to resolve a dot-method call
      (js-invoke input "showPicker")
      (catch js/Error _ nil))))

(defn native-picker-input
  "Text input showing `display-value` (our format) plus a visually hidden native input of `native-type`
  that only hosts the browser calendar popup.
  
  `parse-text`       typed text -> value | nil (nil means not (or not yet) parseable)
  `native-value`     string in the native input's own format
  `on-native-change` raw native string -> nil, called on picker selection or clear
  `on-change`        called with the parsed value, or nil when the text is cleared"
  [_]
  (let [local-state (reagent/atom {:editing-value nil})
        native-ref (atom nil)]
    (fn [{:keys [native-type native-value on-native-change
                 display-value parse-text placeholder
                 on-change on-focus on-blur class id disabled?]}]
      (let [editing-value (:editing-value @local-state)]
        [:div.datepicker-input-holder
         [:input
          {:type "text"
           :class class
           :id id
           :disabled disabled?
           :placeholder placeholder
           ;; while typing, show exactly what was typed; canonical format returns on blur
           :value (or editing-value display-value "")
           :on-focus on-focus
           :on-change (fn [e]
                        (let [text (dom/event-value e)]
                          (swap! local-state assoc :editing-value text)
                          (if (str/blank? text)
                            (on-change nil)
                            (when-let [parsed (parse-text text)]
                              (on-change parsed)))))
           :on-blur (fn [e]
                      ;; unparseable leftovers revert to the last good value
                      (swap! local-state assoc :editing-value nil)
                      (when on-blur (on-blur e)))}]
         [:input.datepicker-native-input
          {:type native-type
           :ref #(reset! native-ref %)
           :value (or native-value "")
           :tab-index -1
           :aria-hidden true
           :disabled disabled?
           :on-change (fn [e]
                        (swap! local-state assoc :editing-value nil)
                        (on-native-change (dom/event-value e)))}]
         [:span.datepicker-input-icon
          {:on-click (fn [_]
                       (when-not disabled?
                         (open-native-picker! @native-ref)))}
          [icons/mux-calendar-manual]]]))))

(defn date-picker-input
  "Plain date. `value` is a tick date or ISO string
  
  `on-change` receives a tick date, or nil when cleared."
  [{:keys [value on-change on-focus on-blur class id disabled?] :as _props}]
  (let [date (as-tick-date value)]
    [native-picker-input
     {:native-type "date"
      :native-value (format-date-for-native-input date)
      :on-native-change (fn [s]
                          (on-change (parse-native-input-value s)))
      :display-value (when date
                       (tick/format fu/local-date-formatter date))
      :parse-text parse-typed-date
      :placeholder date-placeholder
      :on-change on-change
      :on-focus on-focus
      :on-blur on-blur
      :class class
      :id id
      :disabled? disabled?}]))

(defn local-date-time-picker
  "Wall-clock date-time, no timezone. `value` is a tick date-time or nil.
  
  `on-change` receives a tick date-time, or nil when cleared."
  [{:keys [value on-change class id disabled?]}]
  [native-picker-input
   {:native-type "datetime-local"
    :native-value (when value
                    (tick/format native-date-time-formatter value))
    :on-native-change (fn [s]
                        (on-change (parse-native-date-time-value s)))
    :display-value (when value
                     (tick/format fu/date-time-formatter value))
    :parse-text parse-typed-date-time
    :placeholder date-time-placeholder
    :on-change on-change
    :class class
    :id id
    :disabled? disabled?}])
